import { useState, useCallback, useRef } from "react";
import type { MachineEvent } from "@/machines/types/event";

interface UseEventResizeProps {
  svgRef: React.RefObject<SVGSVGElement>;
  scaledWidth: number;
  min: number;
  max: number;
  snapToBoundary?: (timestamp: number, events: any[], type: 'start' | 'end') => number | null;
  machineEvents?: any[];
}

interface ResizeState {
  event: MachineEvent;
  handle: "start" | "end";
  originalStart: number;
  originalEnd: number;
  initialX: number;
}

export const useEventResize = ({
  svgRef,
  scaledWidth,
  min,
  max,
  snapToBoundary,
  machineEvents = [],
}: UseEventResizeProps) => {
  const [resizeState, setResizeState] = useState<ResizeState | null>(null);
  const [tempEventTimes, setTempEventTimes] = useState<{
    start: number;
    end: number;
  } | null>(null);

  // Use ref to always have the latest tempEventTimes value
  const tempEventTimesRef = useRef<{ start: number; end: number } | null>(null);

  const xToTime = useCallback(
    (x: number): number => {
      return min + (x / scaledWidth) * (max - min);
    },
    [min, max, scaledWidth]
  );

  const handleResizeStart = useCallback(
    (event: MachineEvent, handle: "start" | "end", clientX: number) => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;

      const startTime = new Date(event.startTime).getTime();
      const endTime = new Date(event.endTime).getTime();

      const initialTimes = {
        start: startTime,
        end: endTime,
      };

      setResizeState({
        event,
        handle,
        originalStart: startTime,
        originalEnd: endTime,
        initialX: clientX,
      });

      setTempEventTimes(initialTimes);
      tempEventTimesRef.current = initialTimes;
    },
    [svgRef]
  );

  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (!resizeState || !svgRef.current) {
        return;
      }

      const rect = svgRef.current.getBoundingClientRect();
      const deltaX = e.clientX - resizeState.initialX;
      const deltaTime = (deltaX / rect.width) * (max - min);

      let newStart = resizeState.originalStart;
      let newEnd = resizeState.originalEnd;

      if (resizeState.handle === "start") {
        newStart = Math.max(min, Math.min(resizeState.originalEnd - 1000, resizeState.originalStart + deltaTime));

        // Apply snapping if function provided
        if (snapToBoundary) {
          const snappedTime = snapToBoundary(newStart, machineEvents, 'start');
          if (snappedTime !== null) {
            newStart = snappedTime;
          }
        }
      } else {
        newEnd = Math.min(max, Math.max(resizeState.originalStart + 1000, resizeState.originalEnd + deltaTime));

        // Apply snapping if function provided
        if (snapToBoundary) {
          const snappedTime = snapToBoundary(newEnd, machineEvents, 'end');
          if (snappedTime !== null) {
            newEnd = snappedTime;
          }
        }
      }


      const newTimes = { start: newStart, end: newEnd };

      // Update both state and ref
      setTempEventTimes(newTimes);
      tempEventTimesRef.current = newTimes;
    },
    [resizeState, svgRef, min, max, snapToBoundary, machineEvents]
  );

  const handleResizeEnd = useCallback(() => {
    // Use ref to get the latest tempEventTimes value
    const latestTempEventTimes = tempEventTimesRef.current;

    console.log('[useEventResize] handleResizeEnd called:', {
      hasResizeState: !!resizeState,
      hasTempEventTimes: !!latestTempEventTimes,
      resizeState,
      tempEventTimesFromRef: latestTempEventTimes
    });

    if (!resizeState || !latestTempEventTimes) {
      setResizeState(null);
      setTempEventTimes(null);
      tempEventTimesRef.current = null;
      return;
    }

    console.log('[useEventResize] Returning result:', {
      originalTimes: {
        start: resizeState.originalStart,
        end: resizeState.originalEnd
      },
      newTimes: {
        start: latestTempEventTimes.start,
        end: latestTempEventTimes.end
      },
      hasChanged: latestTempEventTimes.start !== resizeState.originalStart || latestTempEventTimes.end !== resizeState.originalEnd
    });

    // Return the updated times and event
    const result = {
      event: resizeState.event,
      newStart: latestTempEventTimes.start,
      newEnd: latestTempEventTimes.end,
    };

    setResizeState(null);
    setTempEventTimes(null);
    tempEventTimesRef.current = null;

    return result;
  }, [resizeState]);

  const cancelResize = useCallback(() => {
    setResizeState(null);
    setTempEventTimes(null);
  }, []);

  return {
    isResizing: !!resizeState,
    resizingEvent: resizeState?.event || null,
    tempEventTimes,
    handleResizeStart,
    handleResizeMove,
    handleResizeEnd,
    cancelResize,
  };
};
